import type { BuildProductTourStepsCtx, ProductTourStep } from './types.js';

const NAV_COPY: Record<string, { title: string; body: string }> = {
  chat: {
    title: 'Chat',
    body: 'Your main conversation with the agent. Tables, files, and rich replies show up here.',
  },
  notifications: {
    title: 'Notifications',
    body: 'Alerts and updates from tasks and the agent. Unread counts appear as a badge.',
  },
  notes: {
    title: 'Note',
    body: 'Capture and browse notes tied to your work (including drafts from chat).',
  },
  tasks: {
    title: 'Tasks',
    body: 'View and manage scheduled or agent-created tasks.',
  },
  admin: {
    title: 'Admin',
    body: 'Invite codes, demo mode, and user admin tools. Same tools as Admin in the left sidebar.',
  },
};

/**
 * Build the ordered product tour for the current session/manifest.
 * Conditional steps (billing, password, admin sidebar) only when controls exist.
 */
export function buildProductTourSteps(ctx: BuildProductTourStepsCtx): ProductTourStep[] {
  const productName = ctx.productName.trim() || 'Agent';
  const steps: ProductTourStep[] = [
    {
      id: 'welcome',
      title: `Welcome to ${productName}`,
      body: 'A short tour of the menu, chat, and your account controls. You can skip anytime.',
      target: null,
    },
  ];

  for (const item of ctx.nav) {
    const known = NAV_COPY[item.id];
    steps.push({
      id: `nav-${item.id}`,
      title: known?.title ?? item.label,
      body:
        known?.body ??
        `A ${productName} page — open it anytime from this menu.`,
      target: `shell-nav-item-${item.id}`,
    });
  }

  steps.push(
    {
      id: 'composer',
      title: 'Start a chat',
      body: 'Type a message and press Send. That creates your first conversation. Starter chips (if any) work too.',
      target: 'chat-composer',
    },
    {
      id: 'sidebar-new-chat',
      title: 'New chat',
      body: 'Start a fresh thread anytime. Past chats stay under Recents.',
      target: 'sidebar-new-chat',
      requiresMobileSidebar: true,
      requiresSidebarExpanded: true,
    },
    {
      id: 'sidebar-commands',
      title: 'Commands',
      body: 'Open slash-command help for this product. Replay this tour from here later.',
      target: 'sidebar-commands',
      requiresMobileSidebar: true,
      requiresSidebarExpanded: true,
    },
    {
      id: 'sidebar-recents',
      title: 'Your chats',
      body: 'History lives here. Click a chat to reopen it; delete when you do not need it.',
      target: 'sidebar-recents',
      requiresMobileSidebar: true,
      requiresSidebarExpanded: true,
    },
  );

  if (ctx.billingEnabled) {
    steps.push({
      id: 'sidebar-billing',
      title: 'Billing',
      body: 'Manage your plan and payment here. Opens over chat — close it anytime to keep chatting.',
      target: 'sidebar-billing',
      requiresMobileSidebar: true,
      requiresSidebarExpanded: true,
    });
  }

  if (ctx.canChangePassword) {
    steps.push({
      id: 'sidebar-password',
      title: 'Change password',
      body: 'Update your login password here. Use a strong password only you know.',
      target: 'sidebar-password',
      requiresMobileSidebar: true,
      requiresSidebarExpanded: true,
    });
  }

  if (ctx.showSidebarAdmin) {
    steps.push({
      id: 'sidebar-admin',
      title: 'Admin',
      body: 'Also available from the top menu when you are an admin. Invite codes, demo mode, and users.',
      target: 'sidebar-admin',
      requiresMobileSidebar: true,
      requiresSidebarExpanded: true,
    });
  }

  steps.push(
    {
      id: 'sidebar-logout',
      title: 'Log out',
      body: 'End this browser session. You can sign back in anytime.',
      target: 'sidebar-logout',
      requiresMobileSidebar: true,
      requiresSidebarExpanded: true,
    },
    {
      id: 'done',
      title: 'You’re set',
      body: 'Try sending a message. Replay anytime from Commands.',
      target: 'chat-composer',
    },
  );

  return steps;
}
